import { ReactNode } from "react";
import { DataKeys, DataStructure } from "../../types";
import { useLang, useScreenSize } from "../Context";
import { MainFrame, SpacingFrame, Col2ContentFrame } from "../display/Frame";
import { TitleSubtitle, TextBody } from "../display/TextFormat";
import { ImageImport } from "../display/ImageFormat";
import { SlideInAnim, SlideDirection } from "../tools/Animations";
import { healthFocusVisible } from "../health/healthVisualStyles";

const VENUE_ORDER = ["attractions", "transportation"] as const;

function CapabilityApplications({ applications }: { applications: DataKeys }) {
    const { lang } = useLang();

    return (
        <div className="mt-5 flex flex-col gap-2.5 border-t border-grey-100/60 pt-4">
            {VENUE_ORDER.map((venueId) => {
                const app = applications[venueId] as DataKeys | undefined;
                if (!app) return null;

                const label = app["label"]?.[lang] ?? "";
                const examples = (app["examples"]?.[lang] as string[]) ?? [];
                if (!label || examples.length === 0) return null;

                return (
                    <div
                        key={venueId}
                        className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2"
                    >
                        <span
                            className={`w-fit rounded-full border border-teal-300/80 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-600 ${healthFocusVisible}`}
                        >
                            {label}
                        </span>
                        <span className="text-sm leading-relaxed text-grey-500">
                            {examples.join(" · ")}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

interface AgentsCapabilitySectionsProps {
    sectionData: DataKeys;
    imagePath: string;
}

export function AgentsCapabilitySections({
    sectionData,
    imagePath,
}: AgentsCapabilitySectionsProps) {
    const { lang } = useLang();
    const { screenSize } = useScreenSize();
    const capabilities = sectionData["capabilities"] as DataStructure;
    const sectionTitle = sectionData["title"]?.[lang] ?? "";

    const renderTextColumn = (colData: DataKeys, applications?: DataKeys) => {
        const title = colData["title"]?.[lang] ?? "";
        const subtitle = colData["subtitle"]?.[lang] ?? "";
        const text = (colData["text"]?.[lang] as string[]) ?? [];

        return (
            <SpacingFrame spacing={20}>
                <TitleSubtitle title={title} left />
                {subtitle ? <TextBody text={[]} title={subtitle} /> : null}
                {text.length > 0 ? <TextBody text={text} /> : null}
                {applications ? (
                    <CapabilityApplications applications={applications} />
                ) : null}
            </SpacingFrame>
        );
    };

    const renderImageColumn = (colData: DataKeys, direction: SlideDirection) => (
        <SlideInAnim direction={direction} style={{ margin: "auto" }}>
            <ImageImport image={colData["image"]} imagePath={imagePath} />
        </SlideInAnim>
    );

    const rows: ReactNode[] = Object.keys(capabilities)
        .sort((a, b) => Number(a) - Number(b))
        .map((key, index) => {
            const capability = capabilities[key] as DataKeys;
            const row = capability["content"] as DataKeys;
            const applications = capability["applications"] as DataKeys;
            if (!row) return null;

            let left: ReactNode = null;
            let right: ReactNode = null;

            Object.keys(row).forEach((side) => {
                const colData = row[side] as DataKeys;
                const colType = colData["type"];
                if (side === "left") {
                    if (colType === "text") {
                        left = renderTextColumn(colData, applications);
                    } else if (colType === "image") {
                        left = renderImageColumn(colData, "left");
                    }
                } else if (side === "right") {
                    if (colType === "text") {
                        right = renderTextColumn(colData, applications);
                    } else if (colType === "image") {
                        right = renderImageColumn(colData, "right");
                    }
                }
            });

            if (!left) return <div key={key}>{right}</div>;
            if (!right) return <div key={key}>{left}</div>;

            if (screenSize === "Small") {
                return (
                    <Col2ContentFrame key={key}>
                        {index % 2 === 0 ? (
                            <>
                                {right}
                                {left}
                            </>
                        ) : (
                            <>
                                {left}
                                {right}
                            </>
                        )}
                    </Col2ContentFrame>
                );
            }

            return (
                <Col2ContentFrame key={key}>
                    {left}
                    {right}
                </Col2ContentFrame>
            );
        });

    return (
        <MainFrame smallerPadding>
            {sectionTitle ? (
                <div className="mb-16">
                    <TitleSubtitle title={sectionTitle} />
                </div>
            ) : null}
            <SpacingFrame spacing={128}>{rows}</SpacingFrame>
        </MainFrame>
    );
}
